/**
 * Footer component
 * Multi-column footer: brand, account, legal, and company links.
 */

import Image from 'next/image'
import LocaleLink from './LocaleLink'
import { LanguageSwitcher } from './LanguageSwitcher'
import { footerTranslations, FooterLocale } from '../translations/footer'

interface FooterProps {
  locale?: string
}

export function Footer({ locale = 'en' }: FooterProps) {
  const t = footerTranslations[locale as FooterLocale] || footerTranslations.en

  return (
    <footer className="w-full border-t border-night/10 bg-transparent" style={{ paddingBottom: 'calc(3rem + env(safe-area-inset-bottom, 0px))' }}>
      <div className="max-w-7xl mx-auto px-6 py-12 sm:py-16 lg:py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Logo and Brand */}
          <div className="col-span-2 lg:col-span-1">
            <LocaleLink href="/" locale={locale} className="flex items-center gap-3 mb-6 hover:opacity-70 transition-opacity">
              <Image
                src="/images/Logo.svg"
                alt="Finley Logo"
                width={32}
                height={32}
                className="w-7 h-7"
              />
              <h3 className="text-h4 font-display text-night">Finley</h3>
            </LocaleLink>
            {/* Trademark - shows below logo on desktop only */}
            <div className="hidden lg:block mt-8">
              <p className="text-xs text-night/40 flex items-center gap-1">
                <span>&copy;</span>
                <span>{new Date().getFullYear()} {t.copyright}</span>
              </p>
            </div>
          </div>

          {/* Account */}
          <div>
            <h4 className="font-display font-medium text-night mb-4 text-sm uppercase tracking-wide">
              {t.account}
            </h4>
            <ul className="space-y-3 text-sm text-night/70">
              <li>
                <LocaleLink href="/account" locale={locale} className="hover:text-night transition-colors">
                  {t.manageSubscription}
                </LocaleLink>
              </li>
              <li>
                <LocaleLink href="/account-deletion" locale={locale} className="hover:text-night transition-colors">
                  {t.accountDeletion}
                </LocaleLink>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-display font-medium text-night mb-4 text-sm uppercase tracking-wide">
              {t.legal}
            </h4>
            <ul className="space-y-3 text-sm text-night/70">
              <li>
                <LocaleLink href="/privacy-policy" locale={locale} className="hover:text-night transition-colors">
                  {t.privacyPolicy}
                </LocaleLink>
              </li>
              <li>
                <LocaleLink href="/terms-of-service" locale={locale} className="hover:text-night transition-colors">
                  {t.termsOfService}
                </LocaleLink>
              </li>
              <li>
                <LocaleLink href="/cookie-policy" locale={locale} className="hover:text-night transition-colors">
                  {t.cookiePolicy}
                </LocaleLink>
              </li>
              <li>
                <LocaleLink href="/imprint" locale={locale} className="hover:text-night transition-colors">
                  {t.imprint}
                </LocaleLink>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display font-medium text-night mb-4 text-sm uppercase tracking-wide">
              {t.company}
            </h4>
            <ul className="space-y-3 text-sm text-night/70">
              <li>
                <LocaleLink href="/manifesto" locale={locale} className="hover:text-night transition-colors">
                  {t.manifesto}
                </LocaleLink>
              </li>
              <li>
                <a href="mailto:info@joinfinley.com" className="underline hover:text-night transition-colors">
                  info@joinfinley.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom row: Trademark (mobile) and Language Switcher */}
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Trademark - shows only on mobile/tablet */}
          <p className="text-xs text-night/40 flex items-center gap-1 lg:hidden">
            <span>&copy;</span>
            <span>{new Date().getFullYear()} {t.copyright}</span>
          </p>
          {/* Empty div for desktop spacing */}
          <div className="hidden lg:block" />
          {/* Language Switcher */}
          <LanguageSwitcher locale={locale} />
        </div>
      </div>
    </footer>
  )
}
